/**
 * DSSelect Component
 *
 * Select/dropdown wrapper that combines MUI Select with design system styling.
 *
 * Usage:
 * <DSSelect
 *   label="Case Type"
 *   value={caseType}
 *   onChange={(e) => setCaseType(e.target.value)}
 *   options={[
 *     { value: 'diagnosis', label: 'Diagnosis' },
 *     { value: 'treatment', label: 'Treatment Plan' },
 *   ]}
 *   required
 * />
 */

import React from 'react';
import { Select, MenuItem } from '@mui/material';
import FormField from './FormField';

export function DSSelect({
  label,
  value,
  onChange,
  error,
  hint,
  required,
  options,
  placeholder,
  disabled,
  className = '',
  id,
  name,
  ...otherProps
}) {
  // Generate id from name or label if not provided
  const fieldId = id || name || (label ? label.toLowerCase().replace(/[^a-z0-9]+/g, '-') : undefined);

  return (
    <FormField
      label={label}
      error={error}
      hint={hint}
      required={required}
      className={className}
      id={fieldId}
    >
      <Select
        id={fieldId}
        name={name}
        aria-label={label}
        value={value || ''}
        onChange={onChange}
        displayEmpty
        disabled={disabled}
        error={!!error}
        size="small"
        sx={{
          borderRadius: 'var(--radius-md)',
          fontFamily: 'var(--font-body)',
          fontSize: '0.88rem',
          color: 'var(--neutral-900)',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--neutral-300)',
            borderWidth: '1.5px',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--neutral-400)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--teal-600)',
            borderWidth: '1.5px',
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--error)',
          },
          '& .MuiSelect-icon': {
            color: 'var(--neutral-400)',
          },
          '& .MuiSelect-disabled': {
            backgroundColor: 'var(--neutral-50)',
            color: 'var(--neutral-400)',
          },
        }}
        {...otherProps}
      >
        {placeholder && (
          <MenuItem value="" disabled>
            {placeholder}
          </MenuItem>
        )}
        {options?.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormField>
  );
}

export default DSSelect;
